import { and, desc, eq } from 'drizzle-orm'
import { inngest } from '@/lib/inngest'
import { db as defaultDb } from '@/db'
import { aiArtifacts, auditLog, knowledgeEntries, tenantConfigs, ticketMessages, tickets } from '@/db/schema'
import { buildKbUpdateProposalPrompt, KB_UPDATE_PROPOSAL_PROMPT_VERSION } from '../../prompts/kb-update-proposal'
import {
  callKbUpdateProposalLLM,
  type AIGatewayConfig,
  type KbUpdateProposalResult,
} from '@/lib/ai-gateway'
import { slugify } from '@/lib/knowledge'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>

export type KbUpdateProposalInput = {
  ticketId: string
  tenantId: string
  expertId: string
  responseMessageId: string
}

export async function runKbUpdateProposal(
  input: KbUpdateProposalInput,
  options?: {
    db?: DB
    callLLM?: (config: AIGatewayConfig, system: string, user: string) => Promise<KbUpdateProposalResult>
  },
): Promise<{ outcome: 'PROPOSED' | 'NO_CHANGE' }> {
  const db = (options?.db ?? defaultDb) as DB
  const llm = options?.callLLM ?? callKbUpdateProposalLLM

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.tenantId, input.tenantId)))
    .limit(1)

  if (!ticket) throw new Error(`Ticket not found: ${input.ticketId}`)

  const [responseMessage] = await db
    .select()
    .from(ticketMessages)
    .where(and(eq(ticketMessages.id, input.responseMessageId), eq(ticketMessages.ticketId, input.ticketId)))
    .limit(1)

  if (!responseMessage) throw new Error(`Response message not found: ${input.responseMessageId}`)

  const [config] = await db
    .select()
    .from(tenantConfigs)
    .where(eq(tenantConfigs.tenantId, input.tenantId))
    .limit(1)

  if (!config) throw new Error(`Tenant config not found for tenant: ${input.tenantId}`)

  const [latestDraft] = await db
    .select()
    .from(aiArtifacts)
    .where(and(eq(aiArtifacts.ticketId, input.ticketId), eq(aiArtifacts.artifactType, 'suggested_response')))
    .orderBy(desc(aiArtifacts.createdAt))
    .limit(1)

  const existingKnowledge = await db
    .select({ title: knowledgeEntries.title })
    .from(knowledgeEntries)
    .where(eq(knowledgeEntries.tenantId, input.tenantId))
    .limit(20)

  const { system, user } = buildKbUpdateProposalPrompt({
    referenceNumber: ticket.referenceNumber,
    aiDraft: latestDraft?.content ?? null,
    expertResponse: responseMessage.bodySanitized,
    existingKnowledgeTitles: existingKnowledge.map((entry) => entry.title),
    resolutionSeverity: ticket.resolutionSeverity ?? undefined,
    resolutionConcerns: ticket.resolutionConcerns ?? undefined,
  })

  let result: KbUpdateProposalResult
  try {
    result = await llm(
      {
        provider: config.aiProvider,
        model: config.aiModel,
        temperature: config.aiTemperature,
        dataProcessingConsent: config.dataProcessingConsent,
      },
      system,
      user,
    )
  } catch (err) {
    result = buildFallbackProposal(ticket.referenceNumber, responseMessage.bodySanitized, err)
  }

  if (!result.should_create || !result.title || !result.body_markdown) {
    await db.insert(auditLog).values({
      tenantId: input.tenantId,
      actorId: input.expertId,
      actorType: 'ai_agent',
      action: 'knowledge.proposal_skipped',
      entityType: 'ticket',
      entityId: input.ticketId,
      payload: { hook: 'kb-update-proposal', reasoning_trace: result.reasoning_trace },
    })
    return { outcome: 'NO_CHANGE' }
  }

  const slug = result.slug ?? slugify(result.title)
  await db.insert(aiArtifacts).values({
    tenantId: input.tenantId,
    ticketId: input.ticketId,
    sourceMessageId: input.responseMessageId,
    triggerEvent: 'ticket/responded',
    artifactType: 'kb_update_proposal',
    content: `Proposed KB entry: ${result.title}`,
    contentJson: JSON.stringify({
      title: result.title,
      slug,
      body_markdown: result.body_markdown,
      source_ticket_id: input.ticketId,
      source_response_message_id: input.responseMessageId,
      reasoning_trace: result.reasoning_trace,
    }),
    modelUsed: config.aiModel,
    promptVersion: KB_UPDATE_PROPOSAL_PROMPT_VERSION,
    validationStatus: 'valid',
    expertAccepted: null,
  })

  await db.insert(auditLog).values({
    tenantId: input.tenantId,
    actorId: input.expertId,
    actorType: 'ai_agent',
    action: 'knowledge.proposal_created',
    entityType: 'ticket',
    entityId: input.ticketId,
    payload: { hook: 'kb-update-proposal', title: result.title, slug, reasoning_trace: result.reasoning_trace },
  })

  return { outcome: 'PROPOSED' }
}

function buildFallbackProposal(
  referenceNumber: string,
  expertResponse: string,
  err: unknown,
): KbUpdateProposalResult {
  const errorMessage = err instanceof Error ? err.message : 'Unknown KB proposal error'
  return {
    should_create: true,
    title: `Expert response learning from ${referenceNumber}`,
    slug: slugify(`expert-response-learning-${referenceNumber}`),
    body_markdown: [
      '## Expert guidance',
      expertResponse,
      '',
      '## Source',
      `Captured from expert response on ${referenceNumber}.`,
    ].join('\n'),
    reasoning_trace: `Fallback KB proposal used because Hook 6 failed: ${errorMessage}`,
  }
}

export const kbUpdateProposalFn = inngest.createFunction(
  { id: 'kb-update-proposal', retries: 3, triggers: [{ event: 'ticket/responded' }] },
  async ({ event, step }) => {
    return await step.run('run-kb-update-proposal', () =>
      runKbUpdateProposal(event.data as KbUpdateProposalInput),
    )
  },
)
